import React, { FC, useLayoutEffect, useRef, useState } from 'react';
import { Box, Flex, Image, useBreakpointValue } from '@chakra-ui/react';
import Button from 'ts/common/components/gallery/Button';
import CoverPhoto from 'ts/common/components/gallery/CoverPhoto';
import GalleryHeading from 'ts/common/components/gallery/GalleryHeading';
import StudioHeading from 'ts/common/components/gallery/StudioHeading';
import StudioLogo from 'ts/common/components/gallery/StudioLogo';
import { ILayoutProps, contentAnimation, coverAnimation } from './BaseLayout';

const DEFAULT_SLIDESHOW_ASPECT_RATIO = '16:9';

/** Parses a 'W:H' aspect-ratio string (e.g. '9:16') into a width/height ratio number. */
function parseAspectRatio(aspectRatio: Nullable<string> | undefined): number {
    const [w, h] = (aspectRatio ?? DEFAULT_SLIDESHOW_ASPECT_RATIO).split(':').map(Number);

    return w > 0 && h > 0 ? w / h : 1;
}

const AccentLayout: FC<ILayoutProps> = ({
    brandName,
    buttonText,
    buttonTextLarge,
    coverPhoto,
    coverPhotoUrl,
    eventBrandLogo,
    galleryTitle,
    logoAltText,
    onClick
}) => {
    const coverPhotoMaxSize = useBreakpointValue({ base: 'xs', lg: 'sm' });
    const slideshowBoxRef = useRef<HTMLDivElement | null>(null);
    const [slideshowBoxSize, setSlideshowBoxSize] = useState<{
        width: number;
        height: number;
    } | null>(null);

    // CoverPhoto's poster/slideshow layers are all CSS `background-image` on divs — they carry
    // no intrinsic size, so a CSS `aspectRatio` alone (with no other definite dimension) doesn't
    // reliably size them the way a plain `<img>`'s own intrinsic size + maxHeight/maxWidth does.
    // Instead: keep `maxHeight`/`maxWidth` on this box (so it resolves the *same* Chakra size
    // tokens the plain <Image> below does), measure the resulting computed max-width/max-height
    // in pixels — independent of this box's own (possibly collapsed) rendered size — and derive
    // an explicit width/height that fits the *slideshow's own* aspect ratio within those pixel
    // bounds (a standard "contain" fit, the same shape math `object-fit: contain` would use for
    // a replaced element). Runs before paint (`useLayoutEffect`) so there's no visible flash.
    useLayoutEffect(() => {
        const element = slideshowBoxRef.current;

        if (!element) {
            return;
        }

        const maxWidthPx = parseFloat(getComputedStyle(element).maxWidth);
        const maxHeightPx = parseFloat(getComputedStyle(element).maxHeight);

        if (!(maxWidthPx > 0) || !(maxHeightPx > 0)) {
            return;
        }

        const ratio = parseAspectRatio(coverPhoto.slideshowAspectRatio);
        const heightAtMaxWidth = maxWidthPx / ratio;

        setSlideshowBoxSize(
            heightAtMaxWidth <= maxHeightPx
                ? { width: maxWidthPx, height: heightAtMaxWidth }
                : { width: maxHeightPx * ratio, height: maxHeightPx }
        );
    }, [coverPhotoMaxSize, coverPhoto.slideshowAspectRatio]);

    const galleryHeadingContainerFlex = useBreakpointValue({ base: 1, lg: 0 });
    const galleryHeadingContainerPaddingY = useBreakpointValue({ base: 6, lg: 7 });
    const galleryHeadingSize =
        useBreakpointValue({ base: 'sm' as const, lg: 'lg' as const }) ?? 'sm';
    const headerAlign = useBreakpointValue({ base: 'start', lg: 'center' });
    const isFullWidthButton = useBreakpointValue({ base: true, lg: false });
    const studioLogoSize = useBreakpointValue({ base: 'md' as const, lg: 'lg' as const });

    return (
        <Flex
            alignItems="center"
            flexDirection="column"
            height="full"
            onClick={onClick}
            overflowY="auto"
            paddingX={4}
            paddingY={6}
            textAlign="center"
            width="full"
        >
            <Flex
                alignItems={headerAlign}
                minHeight="25%"
                paddingBottom={6}
                animation={contentAnimation}
                opacity={0.01}
            >
                {eventBrandLogo ? (
                    <StudioLogo
                        logo={eventBrandLogo}
                        logoAltText={logoAltText}
                        logoSize={studioLogoSize}
                    />
                ) : (
                    <StudioHeading text={brandName} variant="primary" />
                )}
            </Flex>
            {coverPhoto.mediaType === 'slideshow' ? (
                <Box
                    ref={slideshowBoxRef}
                    maxHeight={coverPhotoMaxSize}
                    maxWidth={coverPhotoMaxSize}
                    width={slideshowBoxSize ? `${slideshowBoxSize.width}px` : undefined}
                    height={slideshowBoxSize ? `${slideshowBoxSize.height}px` : undefined}
                    // Without this, the outer Flex's default flex-shrink:1 can squeeze this
                    // box's *height* (the main axis, since that Flex is flexDirection="column")
                    // to fit available space, without correspondingly touching width — silently
                    // distorting the aspect ratio we just computed. A plain <img> doesn't have
                    // this problem: replaced elements get an intrinsic-ratio-aware min-height
                    // that keeps them from being squashed out of shape by flex-shrink; a plain
                    // div has no such protection, so it must be opted out of shrinking here.
                    flexShrink={0}
                    position="relative"
                    animation={coverAnimation}
                >
                    <CoverPhoto coverPhoto={coverPhoto} coverPhotoUrl={coverPhotoUrl} />
                </Box>
            ) : (
                <Image
                    data-testid="gallery-cover-photo"
                    maxHeight={coverPhotoMaxSize}
                    maxWidth={coverPhotoMaxSize}
                    src={coverPhotoUrl ?? undefined}
                    animation={coverAnimation}
                />
            )}
            <Flex
                alignItems="center"
                alignSelf="stretch"
                flexDirection="column"
                flex={1}
                animation={contentAnimation}
                opacity={0.01}
            >
                <Box flex={galleryHeadingContainerFlex} paddingY={galleryHeadingContainerPaddingY}>
                    <GalleryHeading
                        size={galleryHeadingSize}
                        text={galleryTitle}
                        variant="primary"
                    />
                </Box>
                <Button
                    fullWidth={isFullWidthButton}
                    text={buttonText}
                    textLarge={buttonTextLarge}
                    variant="primary"
                />
            </Flex>
        </Flex>
    );
};

export default AccentLayout;
